import { isMobileOnly } from "react-device-detect";

const SectionTableHeader = ({ viewMode, showRoomView = false }) => {
  // Priority: showRoomView first, then viewMode
  if (showRoomView) {
    // When showRoomView is true, only show "No." and "Name" columns
    const columns = [
      { label: "No.", width: isMobileOnly ? "30%" : 35 },
      {
        label: "Name",
        width: isMobileOnly ? "70%" : 235,
      },
    ];
    return (
      <div
        className="d-flex fw-bold border-top bg-brand-dark text-white"
        style={{ width: "fit-content" }}
      >
        {columns.map((column, index) => (
          <div
            key={index}
            className="p-2 border-end border-secondary"
            style={{
              width:
                typeof column.width === "string"
                  ? column.width
                  : column.width
                  ? `${column.width}px`
                  : undefined,
            }}
          >
            {column.label}
          </div>
        ))}
      </div>
    );
  }

  // If showRoomView is false, use viewMode logic
  const showSpec = viewMode === "spec";
  const showReview = viewMode === "review";

  let columns = [
    { label: "Check", width: isMobileOnly ? "8%" : 60 },
    { label: "No.", width: isMobileOnly ? "10%" : 35 },
    // Name: auto-width in Spec/Review views, fixed width in Design view
    {
      label: "Name",
      width:
        showSpec || showReview
          ? undefined
          : isMobileOnly
          ? undefined
          : undefined,
    },
    { label: "H", width: isMobileOnly ? "10%" : 50 },
    { label: "W", width: isMobileOnly ? "10%" : 50 },
    { label: "D", width: isMobileOnly ? "10%" : 50 },
    { label: "Color", width: isMobileOnly ? "15%" : 90 },
    { label: "Outer Lam", width: isMobileOnly ? "15%" : 80 },
    { label: "Inner Lam", width: isMobileOnly ? "15%" : 80 },
    { label: "Outer Board", width: isMobileOnly ? "15%" : 120 },
    { label: "Inner Board", width: isMobileOnly ? "15%" : 120 },
    { label: "Board", width: isMobileOnly ? "13%" : 105 },
    { label: "Handle", width: isMobileOnly ? "15%" : 120 },
    { label: "Hinge", width: isMobileOnly ? "15%" : 120 },
    { label: "Drawer", width: isMobileOnly ? "15%" : 120 },
    { label: "Mechanism", width: isMobileOnly ? "15%" : 85 },
    { label: "Build", width: isMobileOnly ? "13%" : 130 },
    { label: "Carcass Front Edge", width: isMobileOnly ? "15%" : 90 },
    { label: "Partition Front Edge", width: isMobileOnly ? "15%" : 90 },
    { label: "Front Offset", width: isMobileOnly ? "15%" : 80 },
    { label: "Backsheet", width: isMobileOnly ? "15%" : 80 },
    { label: "Minifix", width: isMobileOnly ? "15%" : 60 },
    { label: "Extras", width: isMobileOnly ? "15%" : 175 },
    { label: "Fillers", width: isMobileOnly ? "13%" : 75 },
    { label: "Price", width: isMobileOnly ? "15%" : 60 },
  ];

  if (isMobileOnly && showSpec) {
    columns = columns.filter(
      (col) => !["H", "W", "D", "Color"].includes(col.label)
    );
  }

  const commonColumns = [
    "Hinge",
    "Drawer",
    "Mechanism",
    "Handle",
    "Extras",
    "Price",
  ];

  const reviewColumns = [
    "Check",
    "Outer Lam",
    "Inner Lam",
    "Outer Board",
    "Inner Board",
    "Carcass Front Edge",
    "Partition Front Edge",
    "Front Offset",
    "Backsheet",
    "Minifix",
  ];

  // Helper function to check if a column should be visible
  const isColumnVisible = (column) => {
    if (
      (column.label === "Fillers" && isMobileOnly) ||
      (column.label === "Spec" && !showSpec) ||
      (["Board", "Build"].includes(column.label) && !showSpec) ||
      (column.label === "Color" && showReview) ||
      (commonColumns.includes(column.label) && !showSpec && !showReview) ||
      (reviewColumns.includes(column.label) && !showReview)
    )
      return false;
    return true;
  };

  // Build visible columns array with indices
  const visibleColumns = columns
    .map((column, index) => ({ ...column, originalIndex: index }))
    .filter((column) => isColumnVisible(column));

  // Column display name mapping
  const columnDisplayNames = {
    "Outer Lam": "Shutter",
    "Inner Lam": "Carcass",
    "Outer Board": "Shutter",
    "Inner Board": "Carcass",
    "Carcass Front Edge": "Carcass Front",
    "Partition Front Edge": "Partition Front",
  };

  // Define column groups - create nested structure
  const columnGroups = [
    {
      label: "Color",
      columns: ["Outer Lam", "Inner Lam"],
    },
    {
      label: "Board",
      columns: ["Outer Board", "Inner Board"],
    },
    {
      label: "Build",
      columns: [
        "Carcass Front Edge",
        "Partition Front Edge",
        "Front Offset",
        "Backsheet",
        "Minifix",
      ],
    },
  ];

  // Build nested header structure
  const buildNestedHeaders = () => {
    const groupMap = {};
    columnGroups.forEach((group) => {
      group.columns.forEach((colLabel) => {
        groupMap[colLabel] = group;
      });
    });

    const headerItems = [];
    let i = 0;

    while (i < visibleColumns.length) {
      const column = visibleColumns[i];
      const group = groupMap[column.label];

      if (group && showReview) {
        // This column is part of a group - collect all columns in the group
        const groupColumns = [];
        let j = i;
        while (
          j < visibleColumns.length &&
          groupMap[visibleColumns[j].label] === group
        ) {
          groupColumns.push(visibleColumns[j]);
          j++;
        }

        // Calculate total width for the group
        const groupWidth = groupColumns.reduce((sum, col) => {
          const w = col.width
            ? typeof col.width === "string"
              ? 0
              : col.width
            : 0;
          return sum + w;
        }, 0);

        headerItems.push({
          type: "group",
          label: group.label,
          children: groupColumns,
          width: groupWidth > 0 ? groupWidth : undefined,
          widthString:
            groupColumns[0]?.width && typeof groupColumns[0].width === "string"
              ? groupColumns[0].width
              : undefined,
        });

        i = j; // Skip all columns in this group
      } else {
        // Regular column (not part of a group or not in review mode)
        headerItems.push({
          type: "regular",
          column,
        });
        i++;
      }
    }

    return headerItems;
  };

  const headerItems = buildNestedHeaders();

  // Render single row with nested cells
  return (
    <div className="d-flex fw-bold border-top bg-brand-dark text-white">
      {headerItems.map((item) => {
        if (item.type === "group" && showReview) {
          // Grouped header cell - contains group label and sub-headers
          const groupWidth =
            item.widthString || (item.width ? `${item.width}px` : undefined);
          return (
            <div
              key={`group-${item.label}`}
              className="d-flex flex-column border-end border-secondary"
              style={{
                width: groupWidth,
              }}
            >
              {/* Group label at top */}
              <div
                className="p-2 text-center"
                style={{
                  borderTop: `1px solid var(--color-dark-1)`,
                  borderLeft: `1px solid var(--color-dark-1)`,
                  borderRight: `1px solid var(--color-dark-1)`,
                  borderRadius: "7px 7px 0 0",
                }}
              >
                {item.label}
              </div>
              {/* Sub-headers row at bottom */}
              <div className="d-flex border-top border-secondary">
                {item.children.map((col) => {
                  const displayName =
                    columnDisplayNames[col.label] || col.label;
                  return (
                    <div
                      key={`sub-${col.label}`}
                      className={`p-2 border-end border-secondary ${
                        !col.width ? "flex-grow-1" : ""
                      } ${
                        !isMobileOnly && col.width < 75 ? "text-center" : ""
                      }`}
                      style={{
                        width:
                          typeof col.width === "string"
                            ? col.width
                            : col.width
                            ? `${col.width}px`
                            : undefined,
                      }}
                    >
                      {displayName}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        } else {
          // Regular header - 2x height, vertically centered
          const displayName =
            columnDisplayNames[item.column.label] || item.column.label;
          const isCheckColumn = item.column.label === "Check" && showReview;
          return (
            <div
              key={`regular-${item.column.label}`}
              className={`p-2 border-end border-secondary d-flex align-items-center justify-content-center ${
                !item.column.width ? "flex-grow-1" : ""
              } ${
                !isMobileOnly && item.column.width < 75 ? "text-center" : ""
              }`}
              style={{
                width:
                  typeof item.column.width === "string"
                    ? item.column.width
                    : item.column.width
                    ? `${item.column.width}px`
                    : undefined,
                minHeight: showReview
                  ? "calc(2 * (1.5rem + 0.5rem * 2 + 1px))"
                  : undefined,
                ...(isCheckColumn && {
                  position: "sticky",
                  left: 0,
                  zIndex: 1020,
                  backgroundColor: "var(--color-dark-1)",
                }),
              }}
            >
              {displayName}
            </div>
          );
        }
      })}
    </div>
  );
};

export default SectionTableHeader;
